import { DatePipe } from '@angular/common';
import { Observable } from 'rxjs';
import { ServerDataSource } from 'angular2-smart-table';
import { Model } from './common/.base.model';
import { EntityConfigTemplate } from './common/entity-config.interface';
import { FormConfigTemplate } from './common/form-config-template.interface';

const dPipe = new DatePipe('en');

export interface PayrollCycle extends Model {
  name: string;
  startDate: string;
  endDate: string;
  runStatus: 'draft' | 'calculating' | 'in_review' | 'issuing' | 'processing' | 'completed' | 'cancelled' | 'failed';
  payrollFrequency?: 'Monthly' | 'Fortnightly' | 'Weekly' | 'Bimonthly';
  frappeId?: string;
  runDate?: string;
}

export const PayrollCycleConfig: EntityConfigTemplate = {
  meta: {
    entityName: 'Payroll Cycle',
    entityPluralName: 'payroll/cycles',
    titleField: 'name',
    view: false,
  },
  name:      { title: 'Payroll Cycle Name' },
  startDate: { title: 'Start Date', type: 'date', valuePrep: (cell, _) => dPipe.transform(cell, 'MMM dd, yyyy') ?? cell },
  endDate:   { title: 'End Date',   type: 'date', valuePrep: (cell, _) => dPipe.transform(cell, 'MMM dd, yyyy') ?? cell },
  runStatus: {
    title: 'Status',
    type: 'html',
    valuePrep: (cell: string) => {
      const map: Record<string, [string, string]> = {
        draft: ['', 'Draft'],
        calculating: ['mis-pill-warning', 'Calculating'],
        in_review: ['mis-pill-info', 'In review'],
        issuing: ['mis-pill-warning', 'Issuing'],
        processing: ['mis-pill-warning', 'Processing'],
        completed: ['mis-pill-success', 'Issued'],
        cancelled: ['mis-pill-danger', 'Cancelled'],
        failed: ['mis-pill-danger', 'Failed'],
      };
      const [pill, label] = map[cell ?? 'draft'] ?? ['', cell];
      return `<span class="mis-pill ${pill}">${label}</span>`;
    },
  },
  payrollFrequency: { title: 'Frequency', default: 'Monthly' },
  runDate: {
    title: 'Run Date',
    type: 'date',
    default: '—',
    valuePrep: (cell, _) => cell ? (dPipe.transform(cell, 'MMM dd, yyyy HH:mm') ?? cell) : '—',
  },
  frappeId:  { title: 'Payroll ID', view: { list: false } },
};

export const PayrollCycleFormConfig: FormConfigTemplate = {
  tabs: [
    {
      title: 'Cycle Details',
      columns: [
        {
          size: 'col-12',
          type: 'form',
          sections: [
            {
              title: 'Payroll Cycle',
              rows: [
                {
                  fields: [
                    { name: 'name', label: 'Cycle Name', type: 'text', required: true, size: 'col-12', placeholder: 'e.g. April 2026' },
                  ],
                },
                {
                  fields: [
                    { name: 'startDate', label: 'Start Date', type: 'date', required: true, size: 'col-4' },
                    { name: 'endDate',   label: 'End Date',   type: 'date', required: true, size: 'col-4' },
                    {
                      name: 'payrollFrequency',
                      label: 'Payroll Frequency',
                      type: 'select',
                      required: true,
                      size: 'col-4',
                      default: 'Monthly',
                      options: [
                        { value: 'Monthly', label: 'Monthly' },
                        { value: 'Fortnightly', label: 'Fortnightly' },
                        { value: 'Weekly', label: 'Weekly' },
                        { value: 'Bimonthly', label: 'Bimonthly' },
                      ],
                    },
                  ],
                },
                {
                  fields: [
                    {
                      name: 'runStatus',
                      label: 'Status',
                      type: 'select',
                      size: 'col-6',
                      default: 'draft',
                      disabled: true,
                      options: [
                        { value: 'draft', label: 'Draft' },
                        { value: 'processing', label: 'Processing' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'cancelled', label: 'Cancelled' },
                      ],
                    },
                    {
                      name: 'runDate',
                      label: 'Run Date',
                      type: 'date',
                      size: 'col-6',
                      disabled: true,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

export abstract class PayrollCycleData {
  abstract get gridDataSource(): ServerDataSource;
  abstract list(pageNumber: number, pageSize: number): Observable<PayrollCycle[]>;
  abstract listAll(): Observable<PayrollCycle[]>;
  abstract get(id: string): Observable<PayrollCycle>;
  abstract update(cycle: PayrollCycle): Observable<PayrollCycle>;
  abstract create(cycle: Partial<PayrollCycle>): Observable<PayrollCycle>;
  abstract delete(id: string): Observable<boolean>;
}
